// People tab — the actual humans in this household.
//
// Phase C reads from the `people` table (post-A.5 split). Distinct from
// the engagement rows shown on Wealth/Tax/Insurance tabs, which list one
// row per service contract. One person can have zero (spouse, child,
// trusted contact) or many (client across multiple services) engagements.
//
// URL stays /crm/households/:id/members for stable bookmarks; the
// visible tab label was renamed to "People" in HouseholdTabBar.

import { useState } from 'react';
import { apiFetch } from '../../../utils/apiFetch';

const ROLE_LABELS = {
  client:          'Client',
  spouse:          'Spouse',
  child:           'Child',
  parent:          'Parent',
  trusted_contact: 'Trusted Contact',
  entity:          'Entity',        // LLC / Inc / business name owned by this household
  other:           'Other',
};

const ROLE_OPTIONS = Object.entries(ROLE_LABELS).map(([value, label]) => ({ value, label }));

const ROLE_BADGE_COLORS = {
  client:          'bg-deep-teal/10 text-deep-teal',
  spouse:          'bg-bullogic-blue/10 text-bullogic-blue',
  child:           'bg-sky-blue/15 text-[#1a7ba1]',
  parent:          'bg-sky-blue/15 text-[#1a7ba1]',
  trusted_contact: 'bg-amber-100 text-amber-700',
  entity:          'bg-navy/10 text-navy',
  other:           'bg-gray-100 text-[#666]',
};

// Visual grouping. Each group has a label, the roles it contains, and the
// default role for its "+ Add" button (chosen by hand so adding a spouse
// to an existing client's group pre-fills 'spouse', etc.).
//
// Order matters: groups render top-to-bottom in this order. Spouses live
// in the Clients group per owner's preference (in wealth planning the
// spouse is typically a co-client, not "family context"). Family is
// reserved for kids, parents, and other relatives who aren't on the
// engagement themselves.
const ROLE_GROUPS = [
  { key: 'clients',          label: 'Clients',          roles: ['client', 'spouse'],        addAs: 'spouse' },
  { key: 'family',           label: 'Family',           roles: ['child', 'parent'],         addAs: 'child' },
  { key: 'trusted_contacts', label: 'Trusted Contacts', roles: ['trusted_contact'],         addAs: 'trusted_contact' },
  { key: 'entities',         label: 'Entities',         roles: ['entity'],                  addAs: 'entity' },
  { key: 'other',            label: 'Other',            roles: ['other'],                   addAs: 'other' },
];

function groupRole(role) {
  return ROLE_GROUPS.find(g => g.roles.includes(role)) || ROLE_GROUPS[ROLE_GROUPS.length - 1];
}

export default function MembersTab({ household, people, members, reload }) {
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Group engagements by person_id so each row can show "BWM, BTS" badges
  // and an annualized $ figure. Engagements without a person_id (which
  // shouldn't happen post-A.5 but defensively) get bucketed under null.
  const engagementsByPerson = members.reduce((acc, m) => {
    const key = m.person_id || '__unlinked__';
    if (!acc[key]) acc[key] = [];
    acc[key].push(m);
    return acc;
  }, {});

  // Bucket people into their visual groups. Within each group, sort by
  // display_order (primary first) then name.
  const sorted = [...people].sort((a, b) => {
    if (a.display_order !== b.display_order) return a.display_order - b.display_order;
    return a.name.localeCompare(b.name);
  });
  const peopleByGroup = ROLE_GROUPS.reduce((acc, g) => { acc[g.key] = []; return acc; }, {});
  for (const p of sorted) {
    peopleByGroup[groupRole(p.role).key].push(p);
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <h2 className="text-[15px] font-bold text-navy">People in this household</h2>
            <p className="text-[11px] text-[#888] mt-0.5">
              {people.length} {people.length === 1 ? 'person' : 'people'} · {members.length} active engagement{members.length === 1 ? '' : 's'}
            </p>
          </div>
          {!adding && (
            <button
              onClick={() => setAdding(true)}
              className="text-[12px] font-bold text-white px-3 py-1.5 bg-navy rounded-[4px] hover:bg-navy/90 transition-colors"
            >
              + Add person
            </button>
          )}
        </div>

        {adding && (
          <div className="mb-4">
            <AddPersonForm
              householdId={household.id}
              onCancel={() => setAdding(false)}
              onCreated={() => { setAdding(false); reload(); }}
            />
          </div>
        )}

        {/* Visual groups: hidden when empty (no people of that role).
            Adding always goes through the single +Add person button at
            top right; the user picks the role in the form. */}
        {ROLE_GROUPS.map(group => {
          const groupPeople = peopleByGroup[group.key];
          if (groupPeople.length === 0) return null;
          return (
            <RoleGroupSection
              key={group.key}
              group={group}
              people={groupPeople}
              engagementsByPerson={engagementsByPerson}
              editingId={editingId}
              setEditingId={setEditingId}
              reload={reload}
            />
          );
        })}

        {people.length === 0 && !adding && (
          <p className="text-[12px] text-[#888] italic py-4 text-center">
            No people in this household yet. Click "Add person" to add the first.
          </p>
        )}

        {/* Defensive: list any engagements that aren't linked to a person.
            Post-Phase-A.5 this should always be empty, but if a manual
            INSERT ever creates an orphan engagement we want to see it. */}
        {engagementsByPerson['__unlinked__']?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-rose/20">
            <h3 className="text-[11px] uppercase tracking-wider text-rose font-bold mb-2">
              ⚠ Engagements not linked to any person
            </h3>
            <p className="text-[11px] text-[#666] mb-2">
              These engagement rows have no person_id and need cleanup. Likely from a manual INSERT.
            </p>
            <ul className="text-[12px] space-y-1">
              {engagementsByPerson['__unlinked__'].map(m => (
                <li key={m.id} className="text-navy">
                  {m.name} · {(m.division || '').toUpperCase()} · {m.service_tier || '—'}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── One role-group section ──────────────────────────────────────
function RoleGroupSection({
  group, people, engagementsByPerson,
  editingId, setEditingId, reload,
}) {
  return (
    <section className="mb-5 last:mb-0">
      <h3 className="text-[11px] uppercase tracking-wider text-deep-teal font-bold mb-2">
        {group.label}
        <span className="ml-2 text-[#aaa] font-normal normal-case tracking-normal">
          {people.length} {people.length === 1 ? 'person' : 'people'}
        </span>
      </h3>
      <div className="space-y-2">
        {people.map(p => (
          <PersonRow
            key={p.id}
            person={p}
            engagements={engagementsByPerson[p.id] || []}
            isEditing={editingId === p.id}
            onEdit={() => setEditingId(p.id)}
            onCancelEdit={() => setEditingId(null)}
            onSaved={() => { setEditingId(null); reload(); }}
            onDeleted={() => { setEditingId(null); reload(); }}
          />
        ))}
      </div>
    </section>
  );
}

// ─── Person row (read mode + edit mode) ──────────────────────────
function PersonRow({ person, engagements, isEditing, onEdit, onCancelEdit, onSaved, onDeleted }) {
  if (isEditing) {
    return <EditPersonForm person={person} engagementCount={engagements.length} onCancel={onCancelEdit} onSaved={onSaved} onDeleted={onDeleted} />;
  }

  const activeEngagements = engagements.filter(e => e.status === 'Active');
  const divs = Array.from(new Set(activeEngagements.map(e => e.division).filter(Boolean))).sort();

  return (
    <div className="flex items-center justify-between gap-4 bg-bone/30 rounded-[4px] px-4 py-3 hover:bg-bone/50 transition-colors">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[14px] font-bold text-navy">{person.name}</span>
          <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-[2px] ${ROLE_BADGE_COLORS[person.role] || ROLE_BADGE_COLORS.other}`}>
            {ROLE_LABELS[person.role] || person.role}
          </span>
          {divs.map(d => (
            <span key={d} className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-[2px] bg-navy/10 text-navy">
              {d}
            </span>
          ))}
        </div>
        <ContactRow person={person} />
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-[11px] text-[#888]">
          {engagements.length === 0
            ? <span className="italic">no engagements</span>
            : `${engagements.length} engagement${engagements.length === 1 ? '' : 's'}`}
        </span>
        <button
          onClick={onEdit}
          className="text-[11px] text-bullogic-blue hover:text-navy transition-colors px-2 py-1"
        >
          Edit
        </button>
      </div>
    </div>
  );
}

// ─── Contact info row ────────────────────────────────────────────
// Renders email + phone (+ relationship nuance) as a clean,
// high-contrast row with icons + clickable mailto/tel links.
// Was previously a single line of muted #666 text that was hard to read.
function ContactRow({ person }) {
  const hasEmail = !!person.email;
  const hasPhone = !!person.phone;
  const hasRel = !!person.relationship_to_primary;

  if (!hasEmail && !hasPhone && !hasRel) {
    return (
      <div className="text-[11px] text-[#aaa] italic mt-1.5">No contact info on file</div>
    );
  }

  return (
    <div className="flex items-center gap-4 flex-wrap mt-1.5">
      {hasEmail && (
        <a
          href={`mailto:${person.email}`}
          className="inline-flex items-center gap-1.5 text-[12px] text-navy hover:text-bullogic-blue transition-colors group"
          title={`Email ${person.name}`}
        >
          <IconMail className="w-3.5 h-3.5 text-[#888] group-hover:text-bullogic-blue transition-colors" />
          <span>{person.email}</span>
        </a>
      )}
      {hasPhone && (
        <a
          href={`tel:${person.phone.replace(/[^\d+]/g, '')}`}
          className="inline-flex items-center gap-1.5 text-[12px] text-navy hover:text-bullogic-blue transition-colors group"
          title={`Call ${person.name}`}
        >
          <IconPhone className="w-3.5 h-3.5 text-[#888] group-hover:text-bullogic-blue transition-colors" />
          <span>{person.phone}</span>
        </a>
      )}
      {hasRel && (
        <span className="inline-flex items-center gap-1.5 text-[12px] text-[#666] italic">
          <IconInfo className="w-3.5 h-3.5 text-[#aaa]" />
          {person.relationship_to_primary}
        </span>
      )}
    </div>
  );
}

// ─── Tiny inline icons ───────────────────────────────────────────
// Heroicons-style outline at 16px viewBox, sized via className.
function IconMail({ className }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="1.5" y="3" width="13" height="10" rx="1.5" />
      <path d="M2 4l6 5 6-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IconPhone({ className }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 2.5h2.5l1.2 3.2-1.6 1.1c.9 1.7 2.3 3.1 4 4l1.1-1.6L13.5 10.5V13a1 1 0 0 1-1 1C7 13.8 2.2 9 2 3.5a1 1 0 0 1 1-1Z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IconInfo({ className }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="8" cy="8" r="6" />
      <line x1="8" y1="7" x2="8" y2="11.5" strokeLinecap="round" />
      <circle cx="8" cy="5" r="0.6" fill="currentColor" />
    </svg>
  );
}

// ─── Add / Edit forms ────────────────────────────────────────────
const FIELDS = [
  { key: 'name',                    label: 'Name *',                  type: 'text',     required: true },
  { key: 'role',                    label: 'Role',                    type: 'select',   options: ROLE_OPTIONS },
  { key: 'email',                   label: 'Email',                   type: 'email' },
  { key: 'phone',                   label: 'Phone',                   type: 'tel' },
  { key: 'date_of_birth',           label: 'Date of birth',           type: 'date' },
  { key: 'relationship_to_primary', label: 'Relationship (nuance)',   type: 'text', placeholder: 'e.g. executor, father-in-law' },
  { key: 'display_order',           label: 'Sort order',              type: 'number',   min: 1, max: 99 },
  { key: 'notes',                   label: 'Notes',                   type: 'textarea' },
];

function AddPersonForm({ householdId, onCancel, onCreated }) {
  return (
    <div className="bg-white rounded-[4px] border-2 border-bullogic-blue p-4">
      <PersonForm
        initial={{ role: 'client', display_order: 1 }}
        submitLabel="Add person"
        onCancel={onCancel}
        onSubmit={async (values) => {
          const res = await apiFetch('/api/data/people', {
            method: 'POST',
            body: JSON.stringify({ ...values, household_id: householdId }),
          });
          if (!res.ok) throw new Error(`Create failed (${res.status}): ${await res.text()}`);
          onCreated();
        }}
      />
    </div>
  );
}

function EditPersonForm({ person, engagementCount, onCancel, onSaved, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const canDelete = engagementCount === 0;

  return (
    <div className="bg-white rounded-[4px] border-2 border-bullogic-blue p-4">
      <PersonForm
        initial={person}
        submitLabel="Save changes"
        onCancel={onCancel}
        onSubmit={async (values) => {
          const res = await apiFetch(`/api/data/people/${encodeURIComponent(person.id)}`, {
            method: 'PATCH',
            body: JSON.stringify(values),
          });
          if (!res.ok) throw new Error(`Save failed (${res.status}): ${await res.text()}`);
          onSaved();
        }}
      />
      <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between">
        <span className="text-[10px] text-[#888]">ID: {person.id}</span>
        <button
          onClick={async () => {
            if (!canDelete) return;
            if (!confirm(`Permanently remove ${person.name}? This can't be undone.`)) return;
            setDeleting(true);
            try {
              const res = await apiFetch(`/api/data/people/${encodeURIComponent(person.id)}`, { method: 'DELETE' });
              if (!res.ok) throw new Error(`Delete failed (${res.status}): ${await res.text()}`);
              onDeleted();
            } catch (e) {
              alert(e.message);
              setDeleting(false);
            }
          }}
          disabled={!canDelete || deleting}
          className={`text-[11px] font-bold px-2 py-1 rounded-[4px] transition-colors ${
            canDelete
              ? 'text-rose hover:bg-rose/10'
              : 'text-[#ccc] cursor-not-allowed'
          }`}
          title={canDelete ? 'Permanently delete this person' : `Cannot delete: ${engagementCount} engagement(s) link to this person`}
        >
          {deleting ? 'Deleting…' : 'Delete person'}
        </button>
      </div>
    </div>
  );
}

function PersonForm({ initial, submitLabel, onCancel, onSubmit }) {
  const [values, setValues] = useState(() => ({
    name: initial?.name || '',
    role: initial?.role || 'client',
    email: initial?.email || '',
    phone: initial?.phone || '',
    date_of_birth: initial?.date_of_birth || '',
    relationship_to_primary: initial?.relationship_to_primary || '',
    display_order: initial?.display_order ?? 1,
    notes: initial?.notes || '',
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!values.name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const cleaned = {
        ...values,
        name: values.name.trim(),
        email: values.email.trim() || null,
        phone: values.phone.trim() || null,
        date_of_birth: values.date_of_birth || null,
        relationship_to_primary: values.relationship_to_primary.trim() || null,
        notes: values.notes.trim() || null,
        display_order: Number(values.display_order) || 1,
      };
      await onSubmit(cleaned);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        {FIELDS.map(f => (
          <div key={f.key} className={f.type === 'textarea' ? 'col-span-2' : ''}>
            <label className="text-[10px] uppercase tracking-wider text-[#888] font-bold mb-1 block">{f.label}</label>
            {f.type === 'select' ? (
              <select
                value={values[f.key]}
                onChange={(e) => setValues(v => ({ ...v, [f.key]: e.target.value }))}
                className="w-full text-[12px] text-navy border border-gray-200 rounded-[4px] px-2 py-1.5 focus:outline-none focus:border-bullogic-blue"
              >
                {f.options.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : f.type === 'textarea' ? (
              <textarea
                value={values[f.key]}
                onChange={(e) => setValues(v => ({ ...v, [f.key]: e.target.value }))}
                rows={2}
                className="w-full text-[12px] text-navy border border-gray-200 rounded-[4px] px-2 py-1.5 focus:outline-none focus:border-bullogic-blue resize-y"
              />
            ) : (
              <input
                type={f.type}
                value={values[f.key]}
                onChange={(e) => setValues(v => ({ ...v, [f.key]: e.target.value }))}
                placeholder={f.placeholder}
                min={f.min}
                max={f.max}
                className="w-full text-[12px] text-navy border border-gray-200 rounded-[4px] px-2 py-1.5 focus:outline-none focus:border-bullogic-blue"
              />
            )}
          </div>
        ))}
      </div>
      {error && <p className="text-[11px] text-rose">{error}</p>}
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={saving}
          className="text-[12px] font-bold text-white px-3 py-1.5 bg-navy rounded-[4px] hover:bg-navy/90 transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving…' : submitLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-[12px] text-[#888] px-3 py-1.5 hover:text-navy transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
